import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { involvementsService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';

const InvolvementEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    role: '',
    organization: '',
    institution: '',
    period: '',
    description: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    if (isEditing) {
      fetchInvolvement();
    }
  }, [id]);

  const fetchInvolvement = async () => {
    try {
      const result = await involvementsService.getById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        toast.error('Failed to load involvement');
      }
    } catch (error) {
      toast.error('Error loading involvement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.role || !formData.organization || !formData.period) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      let result;
      if (isEditing) {
        result = await involvementsService.update(id, formData);
      } else {
        result = await involvementsService.create(formData);
      }

      if (result.success) {
        toast.success(`Involvement ${isEditing ? 'updated' : 'created'} successfully!`);
        setTimeout(() => {
          navigate('/adminaccess/involvements');
        }, 500);
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} involvement`);
      }
    } catch (error) {
      toast.error('Error saving involvement');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/involvements')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Involvement'}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">
            {isEditing ? 'Edit Involvement' : 'Add Involvement'}
          </h1>
          <p className="text-white/60">
            {isEditing ? '' : 'Add a leadership position or organizational role'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Role */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Role / Position <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., President, Co-founder, Vice President"
            />
          </div>

          {/* Organization */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Organization <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., Google Developer Student Club, Upsilon Pi Epsilon"
            />
          </div>

          {/* Institution */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Institution (Optional)
            </label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., Southeastern Louisiana University"
            />
          </div>

          {/* Period */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Time Period <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="period"
              value={formData.period}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., 2023 - Present, Fall 2023 - Spring 2024"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
              placeholder="Brief description of your role and responsibilities..."
            />
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Logo/Image URL (Optional)</label>
            <input
              type="url"
              name="image"
              value={formData.image}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., https://example.com/logo.png"
            />
          </div>

          {/* Order */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Display Order</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="0"
            />
            <p className="text-white/40 text-xs mt-1">
              Lower numbers appear first (0 = first position)
            </p>
          </div>

          {/* Preview */}
          <div className="p-6 rounded-lg border border-white/10 bg-white/5">
            <h3 className="text-sm text-white/40 uppercase tracking-wider mb-4">Preview</h3>
            <div className="flex gap-4">
              {formData.image && (
                <img
                  src={formData.image}
                  alt={formData.organization}
                  className="w-16 h-16 rounded-lg object-cover border border-white/10"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="flex-1">
                <h3 className="text-xl font-medium text-white mb-1">
                  {formData.role || 'Role Title'}
                </h3>
                <div className="text-white/70 mb-1">
                  {formData.organization || 'Organization Name'}
                </div>
                {formData.institution && (
                  <div className="text-white/50 text-sm mb-2">{formData.institution}</div>
                )}
                <div className="text-white/40 text-sm">
                  {formData.period || 'Time Period'}
                </div>
                {formData.description && (
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">
                    {formData.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default InvolvementEditor;